import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import { FiCheckCircle, FiGlobe, FiPackage, FiTruck, FiArrowRight } from 'react-icons/fi';

const Home = () => {
  const categories = [
    {
      title: "Fufu & Flour Products",
      desc: "Plantain fufu flour, cocoyam fufu flour, gari, cassava dough and other African staples.",
      icon: "https://adafrofood.com/files/local-image-small-52.png"
    },
    {
      title: "Palm Products",
      desc: "Palm oil, zomi palm oil, palm nut cream and palm soup products for traditional African cooking.",
      icon: "https://adafrofood.com/files/local-image-small-84.png"
    },
    {
      title: "Fresh Produce",
      desc: "Fresh yam, green plantain, garden eggs and other selected produce.",
      icon: "https://adafrofood.com/files/local-image-small-496.png"
    },
    {
      title: "Grocery & Pantry",
      desc: "Peanut butter, cocoa powder, Hausa koko and selected dry food products.",
      icon: "https://adafrofood.com/files/local-image-small-380.png"
    }
  ];

  const features = [
    {
      title: "Authentic African Taste",
      desc: "Sourced with care to bring the true flavors of Ghana and West Africa to your kitchen or business."
    },
    {
      title: "For Homes & Businesses",
      desc: "Suitable for shops, restaurants, and families. Available in various package sizes to meet your needs."
    },
    {
      title: "Wholesale & Import",
      desc: "Wholesale and import enquiries welcome. We focus on practical supply and reliable service."
    }
  ];

  return (
    <div className="bg-brand-cream min-h-screen">
      {/* Hero Section */}
      <section className="relative bg-brand-green overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src="https://adafrofood.com/files/local-image-small-616.png" 
            alt="Ghana Market Imagery" 
            className="w-full h-full object-cover opacity-20 mix-blend-overlay"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-brand-green via-brand-green/90 to-transparent"></div>
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 py-20 lg:py-32">
          <div className="max-w-2xl">
            <motion.span 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="inline-block py-1 px-3 rounded-full bg-brand-yellow/20 text-brand-yellow font-semibold text-sm mb-6 border border-brand-yellow/30"
            >
              Taste Africa. Supply Scandinavia.
            </motion.span>
            <motion.h1 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="text-4xl md:text-5xl lg:text-6xl font-serif font-bold text-white leading-tight mb-6"
            >
              Authentic African Food Products for Homes, Shops and Wholesale Buyers
            </motion.h1>
            <motion.p 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="text-lg md:text-xl text-gray-200 mb-10 leading-relaxed"
            >
              DSGlobal Foundation brings selected African food products to customers, retailers and wholesale partners with a focus on Ghanaian taste, practical supply and reliable service.
            </motion.p>
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="flex flex-col sm:flex-row gap-4"
            >
              <Link 
                to="/products" 
                className="inline-flex justify-center items-center px-8 py-4 border border-transparent text-base font-bold rounded-md text-brand-green bg-brand-yellow hover:bg-yellow-400 transition-colors shadow-lg"
              >
                View Products
              </Link>
              <Link 
                to="/wholesale" 
                className="inline-flex justify-center items-center px-8 py-4 border-2 border-white text-base font-bold rounded-md text-white hover:bg-white hover:text-brand-green transition-colors"
              >
                Request Wholesale Price List
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Trust Strip */}
      <section className="bg-white border-b border-brand-sand shadow-sm relative z-20 -mt-8 mx-4 sm:mx-8 lg:mx-auto max-w-6xl rounded-lg">
        <div className="grid grid-cols-2 md:grid-cols-4 divide-x divide-y md:divide-y-0 divide-brand-sand">
          {[
            { icon: FiGlobe, text: "Ghanaian food heritage" },
            { icon: FiTruck, text: "Import and export focus" },
            { icon: FiCheckCircle, text: "Wholesale enquiries welcome" },
            { icon: FiPackage, text: "Fresh and packaged products" }
          ].map((item, index) => (
            <div key={index} className="p-6 flex flex-col items-center text-center space-y-3">
              <SafeIcon icon={item.icon} className="w-8 h-8 text-brand-red" />
              <span className="font-semibold text-brand-charcoal text-sm">{item.text}</span>
            </div>
          ))}
        </div>
      </section>

      {/* Featured Categories */}
      <section className="py-24 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-serif font-bold text-brand-charcoal mb-4">Featured Product Categories</h2>
          <div className="w-24 h-1 bg-brand-yellow mx-auto"></div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {categories.map((category, index) => (
            <motion.div 
              key={index}
              whileHover={{ y: -5 }}
              className="bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-all border border-brand-sand group"
            >
              <div className="h-48 bg-brand-sand flex items-center justify-center p-6 relative overflow-hidden">
                <div className="absolute inset-0 bg-brand-green/5 group-hover:bg-brand-green/10 transition-colors"></div>
                <img 
                  src={category.icon} 
                  alt={category.title} 
                  className="w-32 h-32 object-contain relative z-10 drop-shadow-md group-hover:scale-105 transition-transform duration-300"
                />
              </div>
              <div className="p-6">
                <h3 className="text-xl font-bold text-brand-charcoal mb-3">{category.title}</h3>
                <p className="text-gray-600 text-sm leading-relaxed mb-4">{category.desc}</p>
                <Link to="/products" className="text-brand-red font-semibold text-sm flex items-center hover:text-brand-green transition-colors">
                  Explore category <SafeIcon icon={FiArrowRight} className="ml-2" />
                </Link>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="bg-brand-sand py-24 relative bg-pattern">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-serif font-bold text-brand-charcoal mb-4">Why Choose DSGlobal Foundation?</h2>
            <div className="w-24 h-1 bg-brand-green mx-auto"></div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
            {features.map((feature, index) => (
              <div key={index} className="bg-white p-8 rounded-xl shadow-sm border border-gray-100 text-center relative">
                <div className="w-12 h-12 bg-brand-yellow/10 rounded-full flex items-center justify-center mx-auto mb-6">
                  <span className="text-brand-yellow font-bold text-xl">{index + 1}</span>
                </div>
                <h3 className="text-xl font-bold text-brand-charcoal mb-4">{feature.title}</h3>
                <p className="text-gray-600 leading-relaxed">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <img 
            src="https://adafrofood.com/files/local-image-small-637.png" 
            alt="Ghana Flag Detail" 
            className="w-16 h-16 mx-auto mb-8 rounded-full object-cover shadow-sm border-2 border-brand-sand"
          />
          <h2 className="text-3xl md:text-4xl font-serif font-bold text-brand-charcoal mb-6">
            Need African food products for your shop or restaurant?
          </h2>
          <p className="text-lg text-gray-600 mb-10 max-w-2xl mx-auto">
            Send us your enquiry and we will respond with availability, quantities and delivery options. We are ready to be your reliable supply partner.
          </p>
          <Link 
            to="/contact" 
            className="inline-flex justify-center items-center px-8 py-4 border border-transparent text-base font-bold rounded-md text-white bg-brand-red hover:bg-red-800 transition-colors shadow-md"
          >
            Contact DSGlobal Foundation
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Home;